'use strict'

const mapValues_ = require('lodash/mapValues')
const isEqual_ = require('lodash/isEqual')
const isBoolean_ = require('lodash/isBoolean')

const { createImmediateCmd, createQueuedCmd } = require('redux-serial-effects')

const breadcrumbWrapper = require('dbsm-common/src/raven/withBreadcrumbs')

const DatasetError = require('../dataset-controller/DatasetError')

const actionTypes = require('./actionTypes')
const cmdTypes = require('./cmdTypes')
const { updateComponentsBasedOnRecord } = require('./actions')

const TYPE = 'RECORDS'

const isFalse = val => isEqual_(false, val)

const convertToFieldType = (fieldType, value) => {
  switch (fieldType) {
    case 'number': {
      return Number(value)
    }

    case 'boolean': {
      if (typeof value === 'string') {
        return value.toLowerCase() === 'true' || value.toLowerCase() === '1'
      }
      break
    }

    case 'text': {
      if (value != null && typeof value.toString === 'function') {
        return value.toString()
      }
      break
    }
  }

  return value
}

const ensureSchemaTypes = (getFieldType, record) =>
  mapValues_(record, (value, fieldName) =>
    convertToFieldType(getFieldType(fieldName), value)
  )

const executor = (
  recordStore,
  adapterApi,
  getFieldType,
  executeHooks,
  Raven,
  controllerId
) => {
  const updateComponents = updateComponentsBasedOnRecord(adapterApi)

  const sanitiseUserData = ['setFieldsInCurrentRecord']
  // createBreadcrumb :: (String, [*]) -> Breadcrumb
  const createBreadcrumb = (fnName, args) => ({
    category: 'recordsExecutor',
    level: 'info',
    message: `${fnName}(${
      sanitiseUserData.includes(fnName)
        ? `..${args.length} arguments..`
        : args.map(JSON.stringify)
    }) (${controllerId})`,
    data: {}
  })

  const { withBreadcrumbs, withBreadcrumbsAsync } = breadcrumbWrapper(
    Raven,
    createBreadcrumb,
    _ => _
  )

  const executeGetRecordByIndex = withBreadcrumbsAsync(
    'getRecordByIndex',
    index => {
      return recordStore().matchWith({
        Nothing: () => {
          throw new Error('Record store service not ready yet')
        },
        Just: async ({ value: service }) => {
          const queryResult = await service.getRecords(index, 1)

          return queryResult.matchWith({
            Empty: () => {
              return { index: null, record: null }
            },
            Results: ({ items }) => {
              return { index, record: items[0] }
            }
          })
        }
      })
    }
  )

  const executeSetFieldsInCurrentRecord = withBreadcrumbs(
    'setFieldsInCurrentRecord',
    (index, fields, source) => {
      const convertedFieldValues = ensureSchemaTypes(getFieldType, fields)

      return recordStore().chain(service =>
        service.setFieldsValues(index, convertedFieldValues, source).fold(
          e => {
            throw e
          },
          _ => {}
        )
      )
    }
  )

  const executeBeforeSaveHooks = withBreadcrumbsAsync(
    'executeBeforeSaveHooks',
    executeHooks => {
      return executeHooks('beforeSave')
        .then(hookResults => hookResults.some(isFalse))
        .catch(e => ({ error: e }))
        .then(shouldCancelSave => {
          if (shouldCancelSave) {
            throw new DatasetError(
              'DS_OPERATION_CANCELLED',
              `Operation cancelled by user code. ${
                isBoolean_(shouldCancelSave) ? '' : shouldCancelSave.error
              }`
            )
          }
        })
    }
  )

  const checkHasDraft = withBreadcrumbs('checkHasDraft', index => {
    return recordStore().matchWith({
      Nothing: () => false,
      Just: ({ value: service }) => service.hasDraft(index)
    })
  })

  const saveRecord = withBreadcrumbsAsync('saveRecord', index => {
    return recordStore().matchWith({
      Nothing: () => {
        return Promise.reject(new Error('Record store service not ready yet'))
      },
      Just: ({ value: service }) => service.saveRecord(index)
    })
  })

  const removeRecordByIndex = withBreadcrumbsAsync(
    'removeRecordByIndex',
    index =>
      recordStore().matchWith({
        Nothing: () => {
          return Promise.reject(new Error('Record store service not ready yet'))
        },
        Just: async ({ value: service }) => await service.removeRecord(index)
      })
  )

  const newRecordAtIndex = withBreadcrumbs(
    'newRecordAtIndex',
    (atIndex, defaultDraft) =>
      recordStore().matchWith({
        Nothing: () => {
          return Promise.reject(new Error('Record store service not ready yet'))
        },
        Just: ({ value: service }) => service.newRecord(atIndex, defaultDraft)
      })
  )

  return {
    type: TYPE,
    execute: cmd => {
      switch (cmd.type) {
        case cmdTypes.GET_RECORD_BY_INDEX:
          return executeGetRecordByIndex(cmd.index)

        case cmdTypes.SET_FIELDS_IN_CURRENT_RECORD:
          return executeSetFieldsInCurrentRecord(
            cmd.index,
            cmd.fields,
            cmd.source
          )

        case cmdTypes.REVERT_CHANGES:
          recordStore().chain(service =>
            service.resetDraft(cmd.index, cmd.defaultDraft)
          )

          // This updateComponentsBasedOnRecord is still needed because of upload buttons' behaviour.
          // Upload buttons, when a file is selected, do not update the record data, but still
          // need to be reverted to clear that selection. Therefore, the revert action must always
          // call the adapters' currentRecordModified event.
          updateComponents()
          break

        case cmdTypes.CALL_BEFORE_SAVE_HOOKS:
          return executeBeforeSaveHooks(executeHooks)

        case cmdTypes.CHECK_HAS_DRAFT:
          return checkHasDraft(cmd.index)

        case cmdTypes.SAVE_RECORD:
          return saveRecord(cmd.index)
        case cmdTypes.REMOVE_RECORD:
          return removeRecordByIndex(cmd.index)
        case cmdTypes.NEW_RECORD:
          return newRecordAtIndex(cmd.atIndex, cmd.defaultDraft)
      }
    }
  }
}

const getRecordByIndexCmd = index =>
  createQueuedCmd(
    TYPE,
    {
      type: cmdTypes.GET_RECORD_BY_INDEX,
      index
    },
    actionTypes.GET_RECORD_BY_INDEX_RESULT
  )

const setFieldsInCurrentRecordCmd = (fields, index, source) =>
  createImmediateCmd(TYPE, {
    type: cmdTypes.SET_FIELDS_IN_CURRENT_RECORD,
    fields,
    index,
    source
  })

const revertChangesCmd = (index, defaultDraft) =>
  createImmediateCmd(
    TYPE,
    {
      type: cmdTypes.REVERT_CHANGES,
      index,
      defaultDraft
    },
    actionTypes.RECORD_REVERTED
  )

const callBeforeSaveHooksCmd = () =>
  createQueuedCmd(
    TYPE,
    {
      type: cmdTypes.CALL_BEFORE_SAVE_HOOKS
    },
    actionTypes.CALL_BEFORE_SAVE_HOOKS_RESULT
  )

const checkHasDraftCmd = index =>
  createQueuedCmd(
    TYPE,
    {
      type: cmdTypes.CHECK_HAS_DRAFT,
      index
    },
    actionTypes.CHECK_HAS_DRAFT_RESULT
  )

const saveRecordCmd = index =>
  createQueuedCmd(
    TYPE,
    {
      type: cmdTypes.SAVE_RECORD,
      index
    },
    actionTypes.SAVE_RECORD_RESULT
  )

const removeCurrentRecordCmd = index =>
  createQueuedCmd(
    TYPE,
    {
      type: cmdTypes.REMOVE_RECORD,
      index
    },
    actionTypes.REMOVE_CURRENT_RECORD_RESULT
  )
const newRecordCmd = (atIndex, defaultDraft) =>
  createQueuedCmd(
    TYPE,
    { type: cmdTypes.NEW_RECORD, atIndex, defaultDraft },
    actionTypes.NEW_RECORD_RESULT
  )

module.exports.executor = executor

module.exports.callBeforeSaveHooksCmd = callBeforeSaveHooksCmd
module.exports.checkHasDraftCmd = checkHasDraftCmd
module.exports.getRecordByIndexCmd = getRecordByIndexCmd
module.exports.revertChangesCmd = revertChangesCmd
module.exports.saveRecordCmd = saveRecordCmd
module.exports.setFieldsInCurrentRecordCmd = setFieldsInCurrentRecordCmd
module.exports.removeCurrentRecordCmd = removeCurrentRecordCmd
module.exports.newRecordCmd = newRecordCmd



// WEBPACK FOOTER //
// ./records/executor.js